import { t } from 'i18next';
import { isNil } from 'lodash';
import { Controller, Form, FormProvider } from 'react-hook-form';
import {
  Box,
  Button,
  Switch,
  Divider,
  FormGroup,
  TextField,
  Typography,
  FormControl,
  FormHelperText,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useEditContactInformation } from './hooks';
import { SelectFormField, TextFormField } from 'components/DynamicFormFields';
import { theme } from '@store';
import { CustomModal, SaveChangesModal } from '@components';

interface IEditContactInformationProps {
  patient_id: string;
  disabled: boolean;
}

export const EditContactInformation = ({
  patient_id,
  disabled = false,
}: IEditContactInformationProps) => {
  const {
    formMethods,
    handleClose,
    handleOnClick,
    isOpen,
    isSubmitting,
    onSubmit,
    languageOptions,
    patientContactInformation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useEditContactInformation(patient_id);

  const { control, watch, setValue } = formMethods;
  const contactType = watch('type');

  const handleTypeChange = (
    newType: NonNullable<
      'Patient' | 'Caregiver' | 'Wellness coordinator' | undefined
    >,
  ) => {
    setValue('type', newType);

    if (patientContactInformation) {
      const { caregiver_contact_information, patient_contact_information } =
        patientContactInformation;

      if (newType === 'Patient') {
        setValue(
          'do_not_call',
          patient_contact_information.do_not_call || false,
        );
        setValue('language', patient_contact_information.language || 'English');
        setValue('phone_number', patient_contact_information.phone_number);
        setValue(
          'mobile_phone_number',
          patient_contact_information.mobile_phone_number,
        );
        setValue('email', patient_contact_information.email || '');
      } else {
        setValue(
          'do_not_call',
          caregiver_contact_information.do_not_call || false,
        );
        setValue(
          'language',
          caregiver_contact_information.language || 'English',
        );
        setValue('phone_number', caregiver_contact_information.phone_number);
        setValue(
          'mobile_phone_number',
          caregiver_contact_information.mobile_phone_number,
        );
        setValue('email', caregiver_contact_information.email || '');
      }
    }
  };

  return (
    <>
      <Button
        disabled={disabled}
        onClick={handleOnClick}
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          minWidth: 'unset',
        }}
      >
        EDIT
      </Button>
      <CustomModal
        isOpen={isOpen && !disabled}
        onClose={handleClose}
        title={t('patient.detailsCard.editContactInfo.title')}
        isLoading={isSubmitting}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Controller
              name={'type'}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl sx={{ width: '100%' }}>
                  <FormLabel sx={{ width: '100%' }} id={'name'}>
                    {'Select primary contact'}
                  </FormLabel>
                  <RadioGroup
                    defaultValue={'Patient'}
                    {...field}
                    onChange={e =>
                      handleTypeChange(
                        e.target.value as NonNullable<
                          | 'Patient'
                          | 'Caregiver'
                          | 'Wellness coordinator'
                          | undefined
                        >,
                      )
                    }
                  >
                    <FormControlLabel
                      key={'Patient'}
                      value={'Patient'}
                      control={<Radio />}
                      label={'Patient'}
                    />
                    <FormControlLabel
                      key={'Caregiver'}
                      value={'Caregiver'}
                      control={
                        <Radio
                          disabled={
                            !patientContactInformation?.caregiver_available
                          }
                        />
                      }
                      label={'Caregiver'}
                    />
                    <FormControlLabel
                      key={'Wellness coordinator'}
                      value={'Wellness coordinator'}
                      control={
                        <Radio
                          disabled={
                            !patientContactInformation?.wellness_coordinator_available
                          }
                        />
                      }
                      label={'Wellness coordinator'}
                    />
                  </RadioGroup>
                  <FormHelperText error={!isNil(error)}>
                    {error ? error.message : null}
                  </FormHelperText>
                </FormControl>
              )}
            />

            <Divider
              orientation='horizontal'
              sx={{ color: theme.palette.semiBlack.main, width: '100%' }}
            />

            <Typography variant='subtitle2' sx={{ width: '100%' }}>
              Contact information
            </Typography>

            {contactType !== 'Wellness coordinator' && (
              <>
                <TextFormField name='email' label={'Email'} required={false} />

                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    gap: '0.5rem',
                  }}
                >
                  <TextFormField name='phone_number' label={'Phone number'} />
                  <TextFormField
                    name='mobile_phone_number'
                    label={'Mobile phone number'}
                    required={false}
                  />
                </Box>

                <SelectFormField
                  name={'language'}
                  options={languageOptions}
                  label={'Primary spoken language'}
                />

                <Controller
                  name={'do_not_call'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth>
                      <FormGroup>
                        <FormControlLabel
                          control={<Switch checked={field.value} {...field} />}
                          label={'Do not call'}
                        />
                      </FormGroup>
                      <FormHelperText error={!isNil(error)}>
                        {error ? error.message : null}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </>
            )}

            {contactType === 'Wellness coordinator' && (
              <>
                <TextField
                  type='text'
                  size='medium'
                  label={'Operator'}
                  variant='outlined'
                  required={false}
                  value={patientContactInformation?.operator.name}
                  fullWidth
                  disabled
                />

                <TextField
                  type='text'
                  size='medium'
                  label={'Building'}
                  variant='outlined'
                  required={false}
                  value={patientContactInformation?.building.name}
                  fullWidth
                  disabled
                />

                <TextField
                  type='text'
                  size='medium'
                  label={'Wellness coordinator'}
                  variant='outlined'
                  required={false}
                  value={patientContactInformation?.wellness_coordinator?.name}
                  fullWidth
                  disabled
                />

                <TextField
                  type='text'
                  size='medium'
                  label={'Phone number'}
                  variant='outlined'
                  required={false}
                  value={
                    patientContactInformation?.wellness_coordinator
                      ?.phone_number
                  }
                  fullWidth
                  disabled
                />
              </>
            )}

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting || isEditSubmitting}
                loading={isEditSubmitting}
                loadingPosition='start'
              >
                {t(`general.${isEditSubmitting ? 'saving' : 'save'}`)}
              </Button>
            </Box>
            <SaveChangesModal
              isOpen={isSaveChangesModalOpen}
              onClose={handleCloseSaveChangesModal}
              isValid={isValid}
              handleSaveChanges={handleSaveChanges}
              discardChanges={handleDiscardChanges}
              isLoading={isEditSubmitting}
            />
          </Form>
        </FormProvider>
      </CustomModal>
    </>
  );
};
